import { useCallback } from 'react';

import {
  useCheckoutContext,
  Article,
  Price,
  CartMessages,
  Discounts,
  Fee,
  CartTrackingObject,
  CartTrackingArticle,
} from '@zooplus/checkout-state';
import { trackEvent as trackEventUsingLibrary } from '@zooplus/tracker-listener';

import { logError } from '@services/sentry-service';
import {
  CHECKOUT_STATE_PROJECT_ID,
  ADOBE_TRACKING,
  TRACKING_EVENT_TYPE,
  AREA_TITLE,
  CART_VERSION,
  TRACKING_DATA_EVENT,
  TRACKING_DATA_PAGE,
} from '@utilities/constants';
import {
  getCartArticles,
  getCartDiscounts,
  getCartFees,
  getCommonTrackingData,
} from '@utilities/data-tracking';
import { splitShopId, getSellerType } from '@utilities/utils';

import useAdobeTracking from './use-adobe-tracking';

const useCartTracking = () => {
  const { sendAdobeEvent, trackEvent } = useAdobeTracking();
  const { appConfig, cartVersion, checkoutState } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const {
    CART_VIEW,
    OVERWEIGHT_FEE,
    CLICK_EVENT_GENERIC,
    OVERWEIGHT_FEE_DETAIL,
    CART_ADDITION,
    CART_REMOVAL,
    ARTICLE_READDITION,
    ABO_TOGGLE,
    ABO_TOGGLE_ON,
    ABO_TOGGLE_OFF,
    CHECKOUT_BUTTON_CLICK,
    CHECKOUT_BUTTON_IMPRESSION,
    CHECKOUT_BUTTON_CLICK_TRACKING,
    CHECKOUT_BUTTON_IMPRESSION_ENABLED_TRACKING,
    CHECKOUT_BUTTON_IMPRESSION_DISABLED_TRACKING,
    CART_OVERVIEW_PAGE_IDENTIFIER,
    CONTINUE_SHOPPING,
    EMPTY_CART_PAGE_IDENTIFIER,
  } = ADOBE_TRACKING;
  const cart = checkoutState?.cart;
  const logisticFee = cart?.logisticFee;

  const { CLICK_EVENT, IMPRESSION_EVENT } = TRACKING_EVENT_TYPE;

  const getCartTrackingObject = () => {
    if (!cart || !appConfig) {
      return;
    }

    const cartTrackingObject: CartTrackingObject = {
      cartId: cart.cartId,
      selectedDeliveryServiceId: cart.selectedDeliveryServiceId,
      sid: cart.sid,
      siteId: appConfig.shop?.siteId,
      locale: appConfig.locale ?? '',
      summary: { grandTotal: cart.summary.grandTotal, subTotal: cart.summary.subTotal },
      customerType: checkoutState?.customer?.customerType ?? '',
      cartMessages: cart.cartMessages.map((message: CartMessages) => {
        return {
          type: message.type,
          messageKey: message.messageKey,
          placements: message.placements,
        };
      }),
      currency: cart.currency,
      discounts: cart.discounts.map((discount: Discounts) => {
        return {
          couponCode: discount.couponCode,
          type: discount.type,
          selCode: discount.selCode,
          newCustomer: discount.newCustomer,
          freeShipping: discount.freeShipping,
          subtotal: discount.subtotal,
          eligibleArticles: discount.eligibleArticles,
          productGroupBased: discount.productGroupBased,
        };
      }),
      fees: cart.fees.map((fee: Fee) => {
        return {
          absolutePrice: fee.absolute_price ?? fee.absolutePrice,
          type: fee.type,
          subtotal: fee.subtotal ?? fee.subTotal,
        };
      }),
      articles: cart.articles.map((article: Article): CartTrackingArticle => {
        return {
          variantName: article.variantName,
          id: article.id,
          shopId: article.shopId,
          psId: article.shopId,
          type: article.type,
          category: article.category,
          name: article.name,
          actualPrice: article.actualPrice,
          taxValue: article.taxValue,
          quantity: article.quantity,
          voucherApplied: article.voucherApplied,
          subscriptionSelected: article.subscriptionSelected,
          subscriptionDetails: article.subscriptionDetails,
          articleBasedVoucher: article.articleBasedVoucher,
          sellerType: getSellerType(article.shipmentId),
        };
      }),
      newCustomer: cart.newCustomer,
    };
    return cartTrackingObject;
  };

  const trackCartView = useCallback(() => {
    if (cart) {
      sendAdobeEvent({
        event: CART_VIEW,
        cart: getCartTrackingObject(),
      });
    }
  }, [cart]);

  const trackCartViewUsingTrackerListener = useCallback(async () => {
    if (!cart) {
      return;
    }

    const isCartApiV2 = cartVersion?.toUpperCase() === CART_VERSION.V2?.toUpperCase();
    const subscriptionInterval = isCartApiV2
      ? cart.cartAutoshipment?.interval
      : cart.cartSubscription?.interval;

    try {
      trackEventUsingLibrary({
        event: TRACKING_DATA_EVENT.VIEW_CART,
        page: TRACKING_DATA_PAGE.CHECKOUT_CART,
        detail: {
          ...getCommonTrackingData(),
          sid: cart.sid,
          cart: {
            value: cart.summary?.grandTotal || 0,
            articles: getCartArticles(cart.articles, isCartApiV2, subscriptionInterval),
            discounts: getCartDiscounts(cart.discounts, isCartApiV2),
            fees: getCartFees(cart.fees),
          },
        },
      });
    } catch (err) {
      logError(
        new Error(
          `An error occured while trying to send VIEW_CART event via @zooplus/tracker-listener: ${err}`,
        ),
      );
    }
  }, [cart, cartVersion]);

  const trackOverweightFee = useCallback(() => {
    sendAdobeEvent({
      event: OVERWEIGHT_FEE,
      ...logisticFee,
    });
  }, [logisticFee]);

  const trackOverweightFeeDetailsClick = () => {
    trackEvent(CLICK_EVENT_GENERIC, OVERWEIGHT_FEE_DETAIL);
  };

  const trackArticleInteractionEvent = (
    eventType: string,
    quantity: number,
    amount: string,
    shopIdentifier: string,
    variantID: string,
    {
      id,
      price,
      voucherApplied,
      shipmentId,
    }: Pick<Article, 'id' | 'price' | 'voucherApplied' | 'shipmentId'>,
  ) => {
    const eventDetails: {
      articleId: number;
      currencyCode: string | undefined;
      shopIdentifier: string;
      quantity: number;
      unitPrice: number | undefined | string | Price;
      variantID: string;
      isAbd: boolean | undefined;
      sellerType?: string;
      total?: string;
      subtotal?: string;
    } = {
      articleId: id,
      currencyCode: checkoutState?.cart?.currency,
      shopIdentifier: shopIdentifier,
      quantity: quantity,
      unitPrice: price,
      variantID: variantID,
      isAbd: voucherApplied,
      ...(eventType === CART_ADDITION ? { total: amount } : {}),
      ...(eventType === CART_REMOVAL ? { subtotal: amount } : {}),
      sellerType: getSellerType(shipmentId),
    };

    trackEvent(eventType, eventType, eventDetails);
  };

  const trackReAddArticleEvent = (articleId: number, type: string) => {
    trackEvent(ARTICLE_READDITION, ARTICLE_READDITION, {
      articleId: articleId,
      quantity: 1,
      type: type,
    });
  };

  const trackAboToggle = (
    articleId: number,
    shopId: string,
    quantity: number,
    unitPrice: number,
    total: number,
    autoshipmentApplied: boolean,
  ) => {
    const [shopIdentifier, variantId] = splitShopId(shopId);
    trackEvent(ABO_TOGGLE, autoshipmentApplied ? ABO_TOGGLE_ON : ABO_TOGGLE_OFF, {
      articleId: articleId,
      currencyCode: checkoutState?.cart?.currency,
      shopIdentifier: shopIdentifier,
      quantity: quantity,
      unitPrice: unitPrice,
      total: total,
      variantID: variantId,
      subscriptionApplied: autoshipmentApplied,
    });
  };

  const trackGoToPreviewButton = (trackEventType: string, isButtonDisabled?: boolean) => {
    const eventType = trackEventType || CLICK_EVENT;
    const trackingType =
      trackEventType === IMPRESSION_EVENT
        ? isButtonDisabled
          ? CHECKOUT_BUTTON_IMPRESSION_DISABLED_TRACKING
          : CHECKOUT_BUTTON_IMPRESSION_ENABLED_TRACKING
        : CHECKOUT_BUTTON_CLICK_TRACKING;
    const targetLink =
      trackEventType === IMPRESSION_EVENT ? CHECKOUT_BUTTON_IMPRESSION : CHECKOUT_BUTTON_CLICK;

    trackEvent(
      CLICK_EVENT_GENERIC,
      trackingType,
      {
        area: AREA_TITLE,
        identifier: CART_OVERVIEW_PAGE_IDENTIFIER,
        targetLink,
      },
      eventType,
    );
  };

  const trackContinueShoppingEvent = () => {
    trackEvent(CLICK_EVENT_GENERIC, CONTINUE_SHOPPING, {
      area: AREA_TITLE,
      identifier: EMPTY_CART_PAGE_IDENTIFIER,
      targetLink: window.location.origin,
    });
  };

  return {
    trackCartView,
    trackOverweightFee,
    trackOverweightFeeDetailsClick,
    trackArticleInteractionEvent,
    trackReAddArticleEvent,
    trackAboToggle,
    trackGoToPreviewButton,
    trackContinueShoppingEvent,
    trackCartViewUsingTrackerListener,
  };
};

export default useCartTracking;
